import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate a session ID that persists for this browser tab
function getSessionId() {
  let sid = sessionStorage.getItem('bm_analytics_sid');
  if (!sid) {
    sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('bm_analytics_sid', sid);
  }
  return sid;
}

// Detect traffic source from URL params or referrer
function getSource() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('fbclid')) return 'facebook';
  if (params.get('igshid')) return 'instagram';
  if (params.get('utm_source')) return params.get('utm_source');
  const ref = document.referrer;
  if (ref.includes('facebook.com') || ref.includes('fb.com')) return 'facebook';
  if (ref.includes('instagram.com')) return 'instagram';
  if (ref.includes('google.com')) return 'google';
  if (ref.includes('twitter.com') || ref.includes('x.com')) return 'twitter';
  if (ref) return 'referral';
  return 'direct';
}

/**
 * Lightweight analytics tracker. Fires on every route change.
 * Tracks: page path, session ID, referrer, source, duration.
 */
export function usePageTracking() {
  const location = useLocation();
  const sessionStart = useRef(Date.now());
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    // Capture source on first page load for signup attribution
    if (!sessionStorage.getItem('bm_signup_source')) {
      const src = getSource();
      if (src !== 'direct') sessionStorage.setItem('bm_signup_source', src);
    }
    // Don't track admin pages
    if (location.pathname.startsWith('/admin')) return;

    const token = localStorage.getItem('bodymax-token') || sessionStorage.getItem('bodymax-token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    axios.post(`${API}/analytics/pageview`, {
      path: location.pathname,
      referrer: document.referrer,
      session_id: sessionId.current,
      source: getSource(),
      is_wtn: !!(typeof window !== 'undefined' && window.WTN),
    }, { headers }).catch(() => {});
  }, [location.pathname]);

  // Track session duration on page unload
  useEffect(() => {
    const handleUnload = () => {
      const duration = Math.round((Date.now() - sessionStart.current) / 1000);
      // Use sendBeacon for reliable delivery on page close
      const data = JSON.stringify({
        session_id: sessionId.current,
        duration_seconds: duration,
      });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/analytics/session-end`, data);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
    };
  }, []);
}
